import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';

// Atoms (app state)
import {
  useOnboardHSAConfigAtom,
  useOnboardAcctConfigAtom,
} from '../../../../atoms';

// API Requests
import { APP_ROUTES } from '../../../../constants';
import { submitHSAFileUpload, updatePrelimDates } from '../../../../api';

// Components
import DateFormatSelector from './DateFormatSelector';
import OnboardContainer from '../../OnboardContainer';
import Datepicker, { DateValueType } from 'react-tailwindcss-datepicker';

// Utils
import {
  HSAColHeader,
  validateHSAFile,
  MAX_PREVIEW_ROWS,
  ValidateHSAResult,
  UNSUPPORTED_DATE_ERR,
} from '../../../../utils';

import { EXPECTED_HSA_HEADERS } from '../../../../utils';

// Max date is determined by the most recent complete month with data.
// 24hr data lag (e.g. May 1st -> March 31 or May 2nd -> April 30th)
const setMaxDate = (date: Date) => {
  const currentDate = date.getDate();
  if (currentDate < 2) {
    date.setMonth(date.getMonth() - 1);
  }
  date.setDate(0);
};

const MAX_DATE = new Date();
setMaxDate(MAX_DATE);

const formatPrelimDate = (date: Date | string) => {
  const output = date instanceof Date ? date : new Date(date + 'T00:00');
  return output.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const COL_ERROR_MSGS: Record<HSAColHeader, string> = {
  Month: 'Uknown date format.',
  Device: '',
  'Customer ID': 'Only numbers [0-9] and "-" allowed',
  'Account name': '',
  'Campaign ID': 'Only numbers [0-9] and "-" allowed',
  'Ad group ID': 'Only numbers [0-9] and "-" allowed',
  'Keyword ID': 'Only numbers [0-9] and "-" allowed',
  'Search keyword': '',
  'Search keyword match type': '',
  'Display URL domain': '',
  'Search Impr. share (Auction Insights)': '',
  'Search overlap rate': '',
  'Position above rate': '',
  'Top of page rate': '',
  'Abs. Top of page rate': '',
  'Search outranking share': '',
};

const DEFAULT_START = new Date(MAX_DATE);
DEFAULT_START.setMonth(DEFAULT_START.getMonth() - 11);
DEFAULT_START.setDate(1);

// Auction Insights File Upload
function HSAFileUploadForm() {
  const navigate = useNavigate();
  const [acctConfig] = useOnboardAcctConfigAtom();
  const [hsaConfig, setHSAConfig] = useOnboardHSAConfigAtom();

  // Prelim Dates
  const [startDate, setStartDate] = useState<DateValueType>({
    startDate: DEFAULT_START,
    endDate: DEFAULT_START,
  });
  const [endDate, setEndDate] = useState<DateValueType>({
    startDate: MAX_DATE,
    endDate: MAX_DATE,
  });

  // Auction Insights File(s)
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
  const [dateFormatOptions, setDateFormatOptions] = useState<string[] | null>(
    null
  );

  // Currently "selected" processed file (Summary)
  const [selectedResult, setSelectedResult] =
    useState<ValidateHSAResult | null>(null);
  const [validations, setValidations] = useState<ValidateHSAResult[]>([]);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const isNextDisabled = useMemo(() => {
    return (
      !startDate?.startDate ||
      !endDate?.endDate ||
      validations.length < 1 ||
      validations.findIndex(({ errorMsg }) => errorMsg) > -1
    );
  }, [startDate, endDate, validations]);

  // Triggers file selection prompt
  const onChooseFileClick = () => {
    if (!inputRef.current) return;
    inputRef.current.click();
    inputRef.current.files = null;
    setUploadFiles(null);
    setFileError(null);
    setSelectedResult(null);
    setValidations([]);
  };

  // Allow "Customer ID" and "Account Name" to be optional if single account provided in "Properites"
  // Values will be mapped/included during BE validation process
  const optionalHeaders = useMemo(() => {
    if (!acctConfig) return;
    const optHeaders: HSAColHeader[] = [];
    const { google_ads } = acctConfig;

    if (
      google_ads?.customer_id.length === 1 &&
      google_ads?.account_name.length === 1
    ) {
      optHeaders.push('Customer ID', 'Account name');
    }

    return optHeaders.length ? optHeaders : undefined;
  }, [acctConfig]);

  const processFiles = async (files: FileList | null, dateFormat?: string) => {
    if (!files) {
      throw new Error('File is required');
    }
    setIsProcessing(true);

    try {
      for (const file of Array.from(files)) {
        const res = await validateHSAFile(file, {
          optionalHeaders,
          dateFormat,
        });

        setSelectedResult(res);
        setValidations((prev) => {
          return [...prev, res];
        });

        if (Array.isArray(res.dateFormat)) {
          setDateFormatOptions(res.dateFormat);
        }
        if (res.errorMsg && files.length === 1) {
          throw new Error(res.errorMsg);
        }
      }
      setUploadFiles(files);

      if (fileError) {
        setFileError(null);
      }
    } catch (err: unknown) {
      let errorMsg = 'Invalid file.';
      if (err instanceof Error && err.message) {
        errorMsg = err.message;
      }
      if (inputRef.current) {
        inputRef.current.files = null;
      }
      setFileError(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Display file with error in Summary by default
  useEffect(() => {
    const err = validations.find(({ errorMsg }) => errorMsg);
    if (err) {
      setSelectedResult(err);
    }
  }, [validations, fileError]);

  // Handle selection of file(s) and validation process
  const onSelectFiles = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = evt.currentTarget;
    await processFiles(files);
  };

  // Format validation results to be displayed in a summary table
  const tableData = useMemo(() => {
    if (
      !selectedResult ||
      selectedResult.errorMsg?.toLowerCase().includes('missing')
    )
      return null;

    const {
      rowNum,
      delimiter,
      currentRow,
      previewRows,
      colHeadersRow,
      previewErrorRows,
    } = selectedResult;

    const isError = !!previewErrorRows.length;

    // Summary table data
    return previewErrorRows.length ? (
      // File w/ errors (highlight error cells)
      previewErrorRows.map(({ rowNum, value, errCols }) => (
        <tr key={rowNum}>
          <td className="px-4 py-2 text-center">{rowNum}</td>
          {value.split(delimiter).map((colValue, indx) => (
            <td
              className={`px-4 py-2 text-center ${
                errCols.includes(indx) ? 'border-4 border-white bg-red-300' : ''
              }`}
              key={indx}
            >
              {colValue}
            </td>
          ))}
        </tr>
      ))
    ) : (
      // Successful file validation (displays first 5 rows and final row)
      <>
        {previewRows.map((rowStr, indx) => {
          const row = rowStr.split(delimiter);
          const rowNum = indx + colHeadersRow;
          return (
            <tr key={rowNum}>
              <td className="px-4 py-2 text-center">{rowNum}</td>
              {row.map((colValue, indx) => (
                <td className="px-4 py-2 text-center" key={indx}>
                  {colValue}
                </td>
              ))}
            </tr>
          );
        })}
        {rowNum > MAX_PREVIEW_ROWS + 1 ? (
          <tr className="px-4 py-2 text-center">
            <td className="px-4 py-2 text-center">
              <p className="leading-none">...</p>
            </td>
          </tr>
        ) : null}
        <tr>
          <td className="px-4 py-2 text-center">{rowNum}</td>
          {currentRow.split(delimiter).map((colValue, indx) => (
            <td
              className={`px-4 py-2 text-center ${isError ? 'bg-red-300' : ''}`}
              key={indx}
            >
              {colValue}
            </td>
          ))}
        </tr>
      </>
    );
  }, [selectedResult]);

  // Handle form submission (PENDING)
  const onSubmit = async () => {
    if (!hsaConfig?.customer_id) {
      throw new Error('Customer ID is required.');
    }
    if (!hsaConfig.hsa_id) {
      throw new Error('HSA ID is required.');
    }
    if (!startDate?.startDate) {
      throw new Error('Start date is required.');
    }
    if (!endDate?.endDate) {
      throw new Error('End date is required.');
    }
    if (!uploadFiles?.length) {
      throw new Error('File is required.');
    }
    if (!selectedResult) {
      throw new Error('Missing file validation results.');
    }
    if (Array.isArray(selectedResult.dateFormat)) {
      throw new Error(UNSUPPORTED_DATE_ERR);
    }

    // Submit Prelim data
    const { data } = await updatePrelimDates(
      hsaConfig.customer_id,
      hsaConfig.hsa_id,
      {
        prelim_end_date: formatPrelimDate(endDate.endDate),
        prelim_start_date: formatPrelimDate(startDate.startDate),
      }
    );
    setHSAConfig(data);

    // Upload HSA File to GCS bucket
    const fileUploadRes = await submitHSAFileUpload(
      hsaConfig.customer_id,
      hsaConfig.hsa_id,
      uploadFiles[0],
      {
        file_date_format_value: selectedResult.dateFormat,
        file_delimiter: selectedResult.delimiter,
        file_header_record_number: selectedResult.colHeadersRow.toString(),
        file_percentage_format: selectedResult.file_percentage_format,
      }
    );

    navigate(APP_ROUTES.ONBOARD_HSA_UPLOAD_SUCCESS);
    return fileUploadRes;
  };

  const displaySummary = useMemo(
    () =>
      selectedResult?.previewErrorRows.length ||
      selectedResult?.previewRows.length ||
      selectedResult?.currentRow,
    [selectedResult]
  );

  const onCancelDateFormatSelection = () => {
    setDateFormatOptions(null);
  };

  const onConfirmDateFormatSelection = async (selectedFormat: string) => {
    // Clear prior validation results
    setValidations([]);

    try {
      if (!selectedFormat) {
        setFileError('Unable to upload file due to unrecognized date format.');
      } else {
        await processFiles(inputRef.current?.files || null, selectedFormat);
      }
    } finally {
      setDateFormatOptions(null);
    }
  };

  // JSX
  return (
    <>
      <OnboardContainer
        header="Auction Insights File"
        isDisabled={isNextDisabled}
        onSubmit={onSubmit}
      >
        {isProcessing ? (
          <div className="fixed top-0 left-0 z-50 bg-gray-600 opacity-50 h-screen w-screen cursor-wait"></div>
        ) : null}
        {/* --- Prelim Dates --- */}
        <div className="w-72">
          <label>From*</label>
          <Datepicker
            popoverDirection="down"
            displayFormat="MM/DD/YYYY"
            onChange={setStartDate}
            value={startDate}
            maxDate={endDate?.endDate ? new Date(endDate.endDate) : MAX_DATE}
            useRange={false}
            asSingle
          />
        </div>
        <div className="w-72">
          <label>To*</label>
          <Datepicker
            popoverDirection="down"
            displayFormat="MM/DD/YYYY"
            onChange={setEndDate}
            value={endDate}
            minDate={
              startDate?.startDate ? new Date(startDate.startDate) : undefined
            }
            maxDate={MAX_DATE}
            useRange={false}
            asSingle
          />
        </div>

        {/* --- Auction Insights File(s) --- */}
        <div className="flex flex-col gap-2">
          <label htmlFor="auction-insights-files">
            Upload Auction Insights file*
          </label>
          <div className="bg-white w-[36rem] py-2 px-4 rounded-lg">
            <button
              type="button"
              className="border border-solid border-gray400 text-sm font-bold text-gray400 px-6 py-1 rounded-full flex items-center gap-1"
              onClick={onChooseFileClick}
            >
              Choose File
            </button>
            {validations.length > 1 ? (
              // Displays list of files (names) processed - Only for multiple files
              <div className="mt-2">
                <ul className="grid grid-cols-2 gap-x-4">
                  {validations.map((result) => (
                    <li
                      className={`inline-flex gap-2 items-center text-sm text-gray400 underline cursor-pointer ${
                        result.name === selectedResult?.name ? 'font-bold' : ''
                      }`}
                      key={result.name}
                      onClick={() => setSelectedResult(result)}
                    >
                      {result.name}
                      <span
                        className={`inline-flex justify-center items-center h-3.5 w-3.5 rounded-full ${
                          result?.errorMsg ? 'bg-red-300' : 'bg-green-400'
                        }`}
                      >
                        <i
                          className={`h-3 w-3 text-black icon-${
                            result?.errorMsg ? 'cancel' : 'check'
                          }`}
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <input
              ref={inputRef}
              id="auction-isnights-files"
              type="file"
              className="hidden"
              onChange={onSelectFiles}
              accept=".csv"
            />
          </div>

          {/* --- Error Messages --- */}
          {selectedResult?.errorMsg || fileError ? (
            <div className="text-error flex flex-col">
              <span className="font-bold">{fileError}</span>
              {selectedResult?.errorData?.headers
                ? selectedResult?.errorData?.headers?.map((colHeader) => (
                    <span key={colHeader} className="italic">
                      {colHeader}
                    </span>
                  ))
                : Object.entries(selectedResult?.errorData || {})
                    .filter(([, numErrors]) => !!numErrors)
                    .map(([header, numErrors]) => (
                      <span key={header} className="text-sm">
                        <span className="italic me-1">{header}:</span>{' '}
                        <span>
                          {COL_ERROR_MSGS[header as HSAColHeader]} (
                          {numErrors!.toLocaleString()} errors)
                        </span>
                      </span>
                    ))}
              {/* Correct column order for uploading file -  helpful guide/ message to user */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="mb-3 text-sm font-semibold text-gray-800">
                  Please ensure the file columns appear in the following order:
                </p>

                <div className="flex flex-wrap gap-2">
                  {EXPECTED_HSA_HEADERS.map((header, index) => (
                    <span
                      key={header}
                      className="inline-flex items-center rounded-md bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-gray-200"
                    >
                      <span className="mr-1 text-gray-400">{index + 1}.</span>
                      {header}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* --- Validation result summary --- */}
        {selectedResult && displaySummary ? (
          <div
            className={
              selectedResult.colHeadersRow > -1 ? 'overflow-x-scroll' : ''
            }
          >
            <div className="flex items-center gap-2 italic text-lg text-gray-700">
              <span className="text-lg font-bold">Summary:</span>{' '}
              <span className="text-sm">{selectedResult.name}</span>
              <span
                className={`inline-flex justify-center items-center h-5 w-5 rounded-full ${
                  selectedResult.errorMsg ? 'bg-red-300' : 'bg-green-400'
                }`}
              >
                <i
                  className={`h-4 w-4 text-black icon-${
                    selectedResult.errorMsg ? 'cancel' : 'check'
                  }`}
                />
              </span>
            </div>
            {selectedResult.colHeadersRow > -1 ? (
              <table className="bg-light table-auto border-collapse mt-4">
                <thead className="text-primarygray text-sm font-normal bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 border-b border-gray-200">Row</th>
                    {selectedResult?.columnHeaders.map((row) =>
                      row.split(selectedResult.delimiter).map((header, idx) => (
                        <th
                          className="px-4 py-2 border-b border-gray-200"
                          key={idx}
                        >
                          {header}
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody className="text-primarygray text-xs font-normal rounded-md divide-y divide-gray-200">
                  {tableData}
                </tbody>
              </table>
            ) : null}
          </div>
        ) : null}
      </OnboardContainer>
      <DateFormatSelector
        dateOptions={dateFormatOptions}
        onCancel={onCancelDateFormatSelection}
        onConfirm={onConfirmDateFormatSelection}
      />
    </>
  );
}

export default HSAFileUploadForm;
